/**
 * StaggeredReveal - Sequential card/element reveal with 4 directions
 *
 * Features:
 * - Scroll-triggered animation via useInView
 * - 4 reveal directions: up, down, left, right
 * - Configurable stagger delay between children
 * - Performance optimized with willChange hints
 */

import { useRef } from "react";
import { motion, useInView, type Variants } from "framer-motion";
import { cn } from "../../utils";

export interface StaggeredRevealProps {
  children: React.ReactNode[];
  className?: string;
  childClassName?: string;
  /** Direction elements appear from. Default: "up" */
  direction?: "up" | "down" | "left" | "right";
  /** Delay between each child animation. Default: 0.08s */
  staggerDelay?: number;
  /** Initial delay before animation starts. Default: 0 */
  initialDelay?: number;
  /** Animation duration. Default: 0.5s */
  duration?: number;
  /** Distance elements travel. Default: 30px */
  distance?: number;
  /** Only animate once. Default: true */
  once?: boolean;
  /** Viewport threshold to trigger. Default: 0.2 */
  threshold?: number;
}

export function StaggeredReveal({
  children,
  className = "",
  childClassName = "",
  direction = "up",
  staggerDelay = 0.08,
  initialDelay = 0,
  duration = 0.5,
  distance = 30,
  once = true,
  threshold = 0.2,
}: StaggeredRevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once, amount: threshold });

  const getInitialPosition = () => {
    switch (direction) {
      case "up":
        return { y: distance };
      case "down":
        return { y: -distance };
      case "left":
        return { x: distance };
      case "right":
        return { x: -distance };
      default:
        return { y: distance };
    }
  };

  const containerVariants: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: initialDelay,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: {
      opacity: 0,
      ...getInitialPosition(),
    },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        duration,
        ease: [0.25, 0.4, 0.25, 1],
      },
    },
  };

  return (
    <motion.div
      ref={ref}
      className={cn(className)}
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      {children.map((child, index) => (
        <motion.div
          key={index}
          className={cn(childClassName)}
          variants={itemVariants}
          style={{ willChange: "transform, opacity" }}
        >
          {child}
        </motion.div>
      ))}
    </motion.div>
  );
}

StaggeredReveal.displayName = "StaggeredReveal";
